import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowLeft,
  Info,
  Target,
  Users,
  Calendar,
  CheckCircle2,
  Zap
} from 'lucide-react';

const Step3Enhanced = ({ formData, setFormData, onNext, onPrev }) => {
  const [errors, setErrors] = useState({});

  const contentCategories = [
    { id: 'electronics', label: 'Electronics & Gadgets', icon: '📱' },
    { id: 'fashion', label: 'Fashion & Style', icon: '👗' },
    { id: 'technology', label: 'Technology', icon: '💻' },
    { id: 'lifestyle', label: 'Lifestyle', icon: '✨' },
    { id: 'education', label: 'Education', icon: '📚' },
    { id: 'hospitality', label: 'Hospitality', icon: '🏨' },
    { id: 'food', label: 'Food & Beverages', icon: '🍔' },
    { id: 'health', label: 'Health & Fitness', icon: '💪' },
    { id: 'entertainment', label: 'Entertainment', icon: '🎬' },
    { id: 'travel', label: 'Travel & Tourism', icon: '✈️' },
    { id: 'sports', label: 'Sports', icon: '⚽' },
    { id: 'beauty', label: 'Beauty & Cosmetics', icon: '💄' },
    { id: 'home', label: 'Home & Living', icon: '🏠' },
    { id: 'business', label: 'Business & Finance', icon: '💼' }
  ];

  const targetAudiences = [
    { id: 'students', label: 'Students', description: 'College, university, and school students', icon: '🎓' },
    { id: 'professionals', label: 'Working Professionals', description: 'Employed individuals and career-focused people', icon: '💼' },
    { id: 'smes', label: 'SMEs', description: 'Small and medium enterprises, business owners', icon: '🏢' },
    { id: 'general', label: 'General Public', description: 'Wide audience across demographics', icon: '👥' }
  ];

  const postingFrequencies = [
    { id: 'daily', label: 'Daily', description: 'I post content every day', icon: '📅' },
    { id: 'weekly', label: 'Weekly', description: 'I post several times per week', icon: '📆', requiresCount: true },
    { id: 'occasional', label: 'Occasional', description: 'I post when I have something to share', icon: '⏰' }
  ];

  const [selectedCategories, setSelectedCategories] = useState(
    formData.contentCategories || []
  );
  const [selectedAudiences, setSelectedAudiences] = useState(
    formData.targetAudiences || []
  );
  const [postingFrequency, setPostingFrequency] = useState(
    formData.postingFrequency || ''
  );
  const [weeklyPostCount, setWeeklyPostCount] = useState(
    formData.weeklyPostCount || ''
  );

  const toggleCategory = (categoryId) => {
    setSelectedCategories(prev => {
      const newSelection = prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId];
      return newSelection;
    });
    
    if (errors.contentCategories) {
      setErrors(prev => ({ ...prev, contentCategories: '' }));
    }
  };

  const toggleAudience = (audienceId) => {
    setSelectedAudiences(prev => {
      const newSelection = prev.includes(audienceId)
        ? prev.filter(id => id !== audienceId)
        : [...prev, audienceId];
      return newSelection;
    });
    
    if (errors.targetAudiences) {
      setErrors(prev => ({ ...prev, targetAudiences: '' }));
    }
  };

  const handlePostingFrequencyChange = (frequency) => {
    setPostingFrequency(frequency);
    if (frequency !== 'weekly') {
      setWeeklyPostCount('');
    }
    
    if (errors.postingFrequency) {
      setErrors(prev => ({ ...prev, postingFrequency: '' }));
    }
  };

  const handleWeeklyPostCountChange = (value) => {
    setWeeklyPostCount(value);
    if (errors.weeklyPostCount) {
      setErrors(prev => ({ ...prev, weeklyPostCount: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (selectedCategories.length === 0) {
      newErrors.contentCategories = 'Please select at least one content category';
    }

    if (selectedAudiences.length === 0) {
      newErrors.targetAudiences = 'Please select at least one target audience';
    }

    if (!postingFrequency) {
      newErrors.postingFrequency = 'Please select your posting frequency';
    }

    if (postingFrequency === 'weekly' && !weeklyPostCount) {
      newErrors.weeklyPostCount = 'Please specify how many times per week you post';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      setFormData(prev => ({
        ...prev,
        contentCategories: selectedCategories,
        targetAudiences: selectedAudiences,
        postingFrequency,
        weeklyPostCount: postingFrequency === 'weekly' ? weeklyPostCount : null
      }));
      onNext();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.5 }}
      className="py-4 mx-auto w-full max-w-6xl"
    >
      {/* Header */}
      <div className="mb-6">
        <h2 className="mb-2 text-2xl font-bold text-gray-900">
          C. CONTENT & AUDIENCE PROFILE
          <span className="ml-2 text-red-600">(MANDATORY)</span>
        </h2>
        <p className="text-gray-600">
          Help us understand your content and audience for better campaign matching
        </p>
      </div>

      {/* UX Guidelines Section */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-4 mb-6 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-200"
      >
        <div className="flex items-start space-x-3">
          <Info className="flex-shrink-0 w-5 h-5 mt-0.5 text-purple-600" />
          <div className="flex-1">
            <h3 className="mb-2 text-sm font-semibold text-purple-900">
              Why we need this information:
            </h3>
            <div className="grid grid-cols-1 gap-2 text-xs text-purple-800 md:grid-cols-3">
              <div className="flex items-start space-x-2">
                <Zap className="flex-shrink-0 w-4 h-4 mt-0.5" />
                <span>Campaign Matching - Get relevant deals for your audience</span>
              </div>
              <div className="flex items-start space-x-2">
                <Target className="flex-shrink-0 w-4 h-4 mt-0.5" />
                <span>Performance Prediction - Optimize your earnings potential</span>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle2 className="flex-shrink-0 w-4 h-4 mt-0.5" />
                <span>Risk Assessment - Ensure quality partnerships</span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Content Categories */}
        <div>
          <div className="flex items-center mb-4 space-x-2">
            <Target className="w-5 h-5 text-gray-600" />
            <label className="text-base font-semibold text-gray-900">
              Content Categories
              <span className="ml-1 text-red-600">*</span>
            </label>
          </div>
          <p className="mb-4 text-sm text-gray-600">
            Select all content categories you create or promote (select multiple)
          </p>
          
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
            {contentCategories.map(category => (
              <motion.button
                key={category.id}
                type="button"
                onClick={() => toggleCategory(category.id)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                  selectedCategories.includes(category.id)
                    ? 'border-red-500 bg-red-50 shadow-md'
                    : 'border-gray-300 bg-white hover:border-gray-400 hover:bg-gray-50'
                }`}
              >
                <div className="flex flex-col items-center space-y-2 text-center">
                  <span className="text-2xl">{category.icon}</span>
                  <span className={`text-sm font-medium ${
                    selectedCategories.includes(category.id) ? 'text-red-700' : 'text-gray-700'
                  }`}>
                    {category.label}
                  </span>
                  {selectedCategories.includes(category.id) && (
                    <CheckCircle2 className="w-5 h-5 text-red-600" />
                  )}
                </div>
              </motion.button>
            ))}
          </div>
          
          {errors.contentCategories && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-2 text-sm text-red-500"
            >
              {errors.contentCategories}
            </motion.p>
          )}
          
          {selectedCategories.length > 0 && (
            <p className="mt-3 text-xs text-green-600">
              ✓ {selectedCategories.length} {selectedCategories.length === 1 ? 'category' : 'categories'} selected
            </p>
          )}
        </div>

        {/* Target Audience */}
        <div>
          <div className="flex items-center mb-4 space-x-2">
            <Users className="w-5 h-5 text-gray-600" />
            <label className="text-base font-semibold text-gray-900">
              Target Audience
              <span className="ml-1 text-red-600">*</span>
            </label>
          </div>
          <p className="mb-4 text-sm text-gray-600">
            Who is your primary audience? (select all that apply)
          </p>
          
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {targetAudiences.map(audience => (
              <motion.button
                key={audience.id}
                type="button"
                onClick={() => toggleAudience(audience.id)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                  selectedAudiences.includes(audience.id)
                    ? 'border-red-500 bg-red-50 shadow-md'
                    : 'border-gray-300 bg-white hover:border-gray-400 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <span className="text-2xl">{audience.icon}</span>
                    <div>
                      <p className={`text-sm font-semibold ${
                        selectedAudiences.includes(audience.id) ? 'text-red-700' : 'text-gray-900'
                      }`}>
                        {audience.label}
                      </p>
                      <p className="mt-1 text-xs text-gray-600">
                        {audience.description}
                      </p>
                    </div>
                  </div>
                  {selectedAudiences.includes(audience.id) && (
                    <CheckCircle2 className="flex-shrink-0 w-5 h-5 text-red-600" />
                  )}
                </div>
              </motion.button>
            ))}
          </div>
          
          {errors.targetAudiences && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-2 text-sm text-red-500"
            >
              {errors.targetAudiences}
            </motion.p>
          )}
          
          {selectedAudiences.length > 0 && (
            <p className="mt-3 text-xs text-green-600">
              ✓ {selectedAudiences.length} {selectedAudiences.length === 1 ? 'audience' : 'audiences'} selected
            </p>
          )}
        </div>

        {/* Posting Frequency */}
        <div>
          <div className="flex items-center mb-4 space-x-2">
            <Calendar className="w-5 h-5 text-gray-600" />
            <label className="text-base font-semibold text-gray-900">
              Posting Frequency
              <span className="ml-1 text-red-600">*</span>
            </label>
          </div>
          <p className="mb-4 text-sm text-gray-600">
            How often do you post content or promotions?
          </p>
          
          <div className="space-y-3">
            {postingFrequencies.map(frequency => (
              <motion.div
                key={frequency.id}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                <button
                  type="button"
                  onClick={() => handlePostingFrequencyChange(frequency.id)}
                  className={`w-full p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                    postingFrequency === frequency.id
                      ? 'border-red-500 bg-red-50 shadow-md'
                      : 'border-gray-300 bg-white hover:border-gray-400 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">{frequency.icon}</span>
                      <div>
                        <p className={`text-sm font-semibold ${
                          postingFrequency === frequency.id ? 'text-red-700' : 'text-gray-900'
                        }`}>
                          {frequency.label}
                        </p>
                        <p className="mt-1 text-xs text-gray-600">
                          {frequency.description}
                        </p>
                      </div>
                    </div>
                    {postingFrequency === frequency.id && (
                      <CheckCircle2 className="flex-shrink-0 w-5 h-5 text-red-600" />
                    )}
                  </div>
                </button>
                
                {/* Weekly Post Count Input */}
                {frequency.requiresCount && postingFrequency === frequency.id && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-3 ml-12"
                  >
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      How many times per week do you post?
                      <span className="ml-1 text-red-600">*</span>
                    </label>
                    <select
                      value={weeklyPostCount}
                      onChange={(e) => handleWeeklyPostCountChange(e.target.value)}
                      className={`px-4 py-3 w-full max-w-xs text-sm text-gray-900 bg-white rounded-lg border transition-all duration-200 focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                        errors.weeklyPostCount ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Select frequency</option>
                      <option value="1-2">1-2 times per week</option>
                      <option value="3-4">3-4 times per week</option>
                      <option value="5-6">5-6 times per week</option>
                      <option value="7+">7+ times per week</option>
                    </select>
                    {errors.weeklyPostCount && (
                      <p className="mt-1 text-xs text-red-500">{errors.weeklyPostCount}</p>
                    )}
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
          
          {errors.postingFrequency && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-2 text-sm text-red-500"
            >
              {errors.postingFrequency}
            </motion.p>
          )}
        </div>
      </form>

      {/* Navigation Buttons */}
      <div className="flex justify-between mt-8">
        <motion.button
          onClick={onPrev}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="flex items-center px-6 py-3 space-x-2 text-gray-700 bg-white rounded-lg border border-gray-300 transition-colors hover:bg-gray-50"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back</span>
        </motion.button>

        <motion.button
          onClick={handleSubmit}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="px-8 py-3 font-medium text-white bg-gradient-to-r from-red-500 to-red-600 rounded-lg shadow-lg transition-all duration-200 shadow-red-500/25 hover:from-red-600 hover:to-red-700 hover:shadow-xl hover:shadow-red-500/40"
        >
          Continue
        </motion.button>
      </div>
    </motion.div>
  );
};

export default Step3Enhanced;
